import React, { useState, useEffect } from "react";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import Header from "../../Common/Layout/Header";
import MasterTabs from "../AllTabs/MasterTabs";
import { useAuth } from "../../Common/Configurations/AuthContext";
import Modal from "react-bootstrap/Modal";
import { Link, useNavigate } from "react-router-dom";
import DeleteAlert from "../../Common/Layout/DeleteAlert";

const initialFieldValues = {
  employeeId: "00000000-0000-0000-0000-000000000000",
  companyId: "00000000-0000-0000-0000-000000000000",
  employeeName: "",
  employeeCode: "",
  email: "",
  phoneNo: "",
  designation: "",
};

const Employee = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [btnLoading, setBtnLoading] = useState(false);
  const [lists, setLists] = useState([]);
  const [values, setValues] = useState(initialFieldValues);
  const [errors, setErrors] = useState({});
  const [recordForEdit, setRecordForEdit] = useState(null);

  // Pagination States
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [data, setData] = useState({}); 

  // UI States
  const [addShow, setAddShow] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // --- DOCUMENT STATES ---
  const [docModalShow, setDocModalShow] = useState(false);
  const [selectedEmp, setSelectedEmp] = useState({ id: null, name: "" });
  const [empDocList, setEmpDocList] = useState([]);
  const [mandatoryFiles, setMandatoryFiles] = useState({ 
    passportFront: null, 
    passportBack: null, 
    passportPhoto: null 
  });
  const [otherDocs, setOtherDocs] = useState([{ documentName: "", documentFile: null }]);

  // --- NEW DOCUMENT FUNCTIONS ---
  const fetchEmployeeDocs = async (id) => {
    try {
      const res = await apiService.get(`${config.GET_EMPLOYEE_DOCUMENTS}/${id}`);
      if (res) setEmpDocList(res);
    } catch (error) {
      console.error("Error fetching docs:", error);
    }
  };

  const handleOpenDocModal = (emp) => {
    setSelectedEmp({ id: emp.employeeId, name: emp.employeeName });
    setDocModalShow(true);
    fetchEmployeeDocs(emp.employeeId);
  };

  const handleDocSubmit = async (e) => {
    e.preventDefault();
    setBtnLoading(true);
    const formData = new FormData();
    formData.append("EmployeeId", selectedEmp.id);
    
    if (mandatoryFiles.passportFront) formData.append("PassportFrontFile", mandatoryFiles.passportFront);
    if (mandatoryFiles.passportBack) formData.append("PassportBackFile", mandatoryFiles.passportBack);
    if (mandatoryFiles.passportPhoto) formData.append("PassportSizePhotoFile", mandatoryFiles.passportPhoto);

    otherDocs.forEach((doc, index) => {
      if (doc.documentFile && doc.documentName) {
        formData.append(`OtherDocuments[${index}].DocumentName`, doc.documentName);
        formData.append(`OtherDocuments[${index}].DocumentFile`, doc.documentFile);
      }
    });

    try {
      await apiService.create(config.CREATE_EMPLOYEE_DOCUMENTS, formData);
      fetchEmployeeDocs(selectedEmp.id);
      setMandatoryFiles({ passportFront: null, passportBack: null, passportPhoto: null });
      setOtherDocs([{ documentName: "", documentFile: null }]);
    } catch (error) {
      console.error("Upload failed:", error);
    } finally {
      setBtnLoading(false);
    }
  };

  const handleDeleteDoc = async (id) => {
    if (window.confirm("Are you sure you want to delete this document?")) {
      await apiService.remove(config.DELETE_EMPLOYEE_DOCUMENT, id);
      fetchEmployeeDocs(selectedEmp.id);
    }
  };

  // Logic to remove a row from Other Documents upload list
  const removeOtherDocRow = (index) => {
    const list = [...otherDocs];
    list.splice(index, 1);
    setOtherDocs(list);
  };

  // --- EXISTING FUNCTIONS (UNTOUCHED) ---
  const handleOpenDeleteModal = (id) => {
    setItemToDelete(id);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  const handleAddClose = () => {
    setAddShow(false);
    setRecordForEdit(null);
    setErrors({});
  };

  const handleAddShow = () => {
    setRecordForEdit(null);
    setValues(initialFieldValues);
    setAddShow(true);
  };

  useEffect(() => {
    if (recordForEdit !== null) {
      setValues(recordForEdit);
    }
  }, [recordForEdit]);

  const GetData = async (pNum) => {
    if (!user?.cid) return;
    try {
      const res = await apiService.get(
        `${config.GETEMPLOYEEBYCOMPANY}/${user?.cid}`,
        pageSize,
        pNum
      );

      if (res) {
        setLists(res.data || []);
        setData(res);
        setPageNumber(res.pageNumber);
        setTotalPages(res.totalPages);
        setTotalRecords(res.totalRecords);
      }
    } catch (error) {
      console.error("Fetch error:", error);
    }
  };

  const GetPageData = (number) => {
    setPageNumber(number);
    if (pageNumber !== number) GetData(number);
  };

  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  const renderPageNumbers = pageNumbers.map((number) => (
    <li
      className={"page-item " + (pageNumber === number ? "active" : "")}
      key={number}
      onClick={() => GetPageData(number)}
    >
      <Link className="page-link">{number}</Link>
    </li>
  ));

  const showEditDetails = (data) => {
    setRecordForEdit(data);
    setValues(data);
    setAddShow(true);
  };

  const handleDelete = async () => {
    if (itemToDelete !== null) {
      await apiService.remove(config.DELETEEMPLOYEE, itemToDelete);
      await GetData(pageNumber);
      handleCloseDeleteModal();
    }
  };

  const validate = () => {
    let temp = {};
    temp.employeeName = values.employeeName !== "";
    temp.employeeCode = values.employeeCode !== "";
    setErrors(temp);
    return Object.values(temp).every((x) => x === true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      setBtnLoading(true);
      const formData = {
        ...values,
        companyId: user?.cid,
      };
      try {
        const isNew = formData.employeeId === "00000000-0000-0000-0000-000000000000";
        if (isNew) {
          await apiService.create(config.CREATEEMPLOYEE, formData);
        } else {
          await apiService.update(config.UPDATEEMPLOYEE, formData);
        }
        await GetData(pageNumber);
        resetForm();
      } finally {
        setBtnLoading(false);
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setValues({ ...values, [name]: value });
  };

  const resetForm = () => {
    setValues(initialFieldValues);
    setAddShow(false);
    setBtnLoading(false);
    setErrors({});
  };

  const applyErrorClass = (field) =>
    field in errors && errors[field] === false ? " is-invalid" : "";

  useEffect(() => {
    if (user && !loading) {
      GetData(pageNumber);
    }
  }, [user, loading]);

  if (loading) return <p>Loading...</p>;
  if (!user) navigate("/");

  return (
    <div id="layout-wrapper">
      <Header User={user} />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            
            <div className="row">
              <div className="col-12">
                <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                  <h4 className="mb-sm-0"></h4>
                  <div className="page-title-right">
                    <ol className="breadcrumb m-0">
                      {/* <li className="breadcrumb-item"><Link to="#">Master</Link></li>
                      <li className="breadcrumb-item active">Employee</li> */}
                    </ol>
                  </div>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-xl-12">
                <div className="card">
                  <div className="p-2">
                    <MasterTabs pageName="EMPLOYEE" />
                  </div>
                  <div className="card-header align-items-center d-flex">
                    <h4 className="card-title mb-0 flex-grow-1">Employee List</h4>
                    <div className="flex-shrink-0">
                      <button className="btn btn-primary add-btn" onClick={handleAddShow}>
                        <i className="ri-add-line align-bottom me-1"></i> Add Employee
                      </button>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="table-responsive table-card">
                      <table className="table align-middle table-nowrap">
                        <thead className="table-light">
                          <tr>
                            <th>#</th>
                            <th>Code</th>
                            <th>Name</th>
                            <th>Designation</th>
                            <th>Email</th>
                            <th>Phone</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {lists.length > 0 ? (
                            lists.map((v, index) => (
                              <tr key={v.employeeId}>
                                <td>{(pageNumber - 1) * pageSize + (index + 1)}</td>
                                <td>{v.employeeCode}</td>
                                <td>{v.employeeName}</td>
                                <td>{v.designation}</td>
                                <td>{v.email}</td>
                                <td>{v.phoneNo}</td>
                                <td>
                                  <div className="hstack gap-3 fs-15">
                                    <button className="btn btn-sm btn-soft-info" title="Documents" onClick={() => handleOpenDocModal(v)}>
                                      <i className="ri-file-list-3-line"></i>
                                    </button>
                                    <Link onClick={() => showEditDetails(v)} className="link-primary"><i className="ri-pencil-fill"></i></Link>
                                    <Link onClick={() => handleOpenDeleteModal(v.employeeId)} className="link-danger"><i className="ri-delete-bin-fill"></i></Link>
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr><td colSpan="7" className="text-center">No Employees Found</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    <div className="align-items-center mt-4 pt-2 justify-content-between d-flex">
                      <div className="text-muted">
                        Showing <span className="fw-semibold">{lists.length}</span> of <span className="fw-semibold">{totalRecords}</span> Results
                      </div>
                      <ul className="pagination pagination-separated pagination-sm mb-0">
                        <li className={`page-item ${data.previousPage === null ? "disabled" : ""}`} onClick={() => GetData(1)}>
                          <Link className="page-link">Previous</Link>
                        </li>
                        {renderPageNumbers}
                        <li className={`page-item ${data.nextPage === null ? "disabled" : ""}`} onClick={() => GetData(totalPages)}>
                          <Link className="page-link">Next</Link>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Existing Add/Edit Modal */}
        <Modal show={addShow} onHide={handleAddClose} centered size="lg">
          <Modal.Header closeButton><Modal.Title>{values.employeeId === "00000000-0000-0000-0000-000000000000" ? "Add" : "Edit"} Employee</Modal.Title></Modal.Header>
          <Modal.Body>
            <form onSubmit={handleSubmit}>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Full Name</label>
                  <input type="text" name="employeeName" value={values.employeeName} onChange={handleInputChange} className={"form-control" + applyErrorClass("employeeName")} placeholder="Enter name" required />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Employee Code</label>
                  <input type="text" name="employeeCode" value={values.employeeCode} onChange={handleInputChange} className={"form-control" + applyErrorClass("employeeCode")} placeholder="Enter code" required />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Email</label>
                  <input type="email" name="email" value={values.email} onChange={handleInputChange} className="form-control" placeholder="Enter email" />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Phone Number</label>
                  <input type="text" name="phoneNo" value={values.phoneNo} onChange={handleInputChange} className="form-control" placeholder="Enter phone" />
                </div>
                <div className="col-md-12">
                  <label className="form-label">Designation</label>
                  <input type="text" name="designation" value={values.designation} onChange={handleInputChange} className="form-control" placeholder="Enter designation" />
                </div>
              </div>
              <div className="mt-4 text-end">
                <button type="button" className="btn btn-light me-2" onClick={resetForm}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={btnLoading}>{btnLoading ? "Processing..." : "Save Employee"}</button>
              </div>
            </form>
          </Modal.Body>
        </Modal>

        {/* --- DOCUMENT POPUP MODAL (MODIFIED UI) --- */}
        <Modal show={docModalShow} onHide={() => setDocModalShow(false)} size="xl" centered>
          <Modal.Header closeButton>
            <Modal.Title>Documents: {selectedEmp.name}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="row g-0">
              {/* Left Side: Perfectly 50% */}
              <div className="col-md-6 border-end pe-4">
                <h6 className="fw-bold mb-3 text-primary">Upload New Documents</h6>
                <form onSubmit={handleDocSubmit}>
                  <div className="mb-2">
                    <label className="form-label small mb-1">Passport Front Pic</label>
                    <input type="file" className="form-control form-control-sm" onChange={(e) => setMandatoryFiles({...mandatoryFiles, passportFront: e.target.files[0]})} />
                  </div>
                  <div className="mb-2">
                    <label className="form-label small mb-1">Passport Back Pic</label>
                    <input type="file" className="form-control form-control-sm" onChange={(e) => setMandatoryFiles({...mandatoryFiles, passportBack: e.target.files[0]})} />
                  </div>
                  <div className="mb-3">
                    <label className="form-label small mb-1">Passport Size Photo</label>
                    <input type="file" className="form-control form-control-sm" onChange={(e) => setMandatoryFiles({...mandatoryFiles, passportPhoto: e.target.files[0]})} />
                  </div>

                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="small fw-bold">Other Documents</span>
                    <button type="button" className="btn btn-sm btn-soft-primary py-0" onClick={() => setOtherDocs([...otherDocs, {documentName: "", documentFile: null}])}>+ Add Row</button>
                  </div>
                  
                  <div style={{maxHeight: '200px', overflowY: 'auto', overflowX: 'hidden'}} className="mb-3 px-1">
                    {otherDocs.map((item, idx) => (
                      <div className="row g-2 mb-2 align-items-center" key={idx}>
                        <div className="col-5">
                          <input type="text" className="form-control form-control-sm" placeholder="Doc Name" value={item.documentName} onChange={(e) => { let d = [...otherDocs]; d[idx].documentName = e.target.value; setOtherDocs(d); }} />
                        </div>
                        <div className="col-5">
                          <input type="file" className="form-control form-control-sm" onChange={(e) => { let d = [...otherDocs]; d[idx].documentFile = e.target.files[0]; setOtherDocs(d); }} />
                        </div>
                        <div className="col-2 text-end">
                          <button type="button" className="btn btn-sm btn-soft-danger" onClick={() => removeOtherDocRow(idx)}>
                            <i className="ri-delete-bin-line"></i>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <button type="submit" className="btn btn-primary btn-sm w-100" disabled={btnLoading}>
                    {btnLoading ? "Uploading..." : "Upload All Documents"}
                  </button>
                </form>
              </div>

              {/* Right Side: Perfectly 50% */}
              <div className="col-md-6 ps-4">
                <h6 className="fw-bold mb-3 text-primary">Existing Documents</h6>
                <div className="table-responsive" style={{maxHeight: '430px'}}>
                  <table className="table table-sm align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>Document Name</th>
                        <th className="text-end">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {empDocList.length > 0 ? (
                        empDocList.map((d) => (
                          <tr key={d.employeeDocumentId}>
                            <td className="small">{d.documentName}</td>
                            <td className="text-end">
                              <div className="hstack gap-2 justify-content-end">
                                <a href={d.imageSrc} target="_blank" rel="noreferrer" className="btn btn-sm btn-soft-primary">
                                  <i className="ri-eye-line"></i>
                                </a>
                                <button type="button" className="btn btn-sm btn-soft-danger" onClick={() => handleDeleteDoc(d.employeeDocumentId)}>
                                  <i className="ri-delete-bin-line"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr><td colSpan="2" className="text-center py-4 text-muted">No documents found</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </Modal.Body>
        </Modal>

        <DeleteAlert show={showDeleteModal} handleClose={handleCloseDeleteModal} handleDelete={handleDelete} />
      </div>
    </div>
  );
};

export default Employee;